import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

type TrackIndex = Awaited<typeof import("@/data/courses")>["tracks"];

interface SearchResult {
  type: "track" | "module" | "lesson" | "tool";
  label: string;
  description: string;
  path: string;
  trackName: string;
}

export function SearchDialog() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [tracks, setTracks] = useState<TrackIndex>([]);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Keyboard shortcut: Ctrl/Cmd + K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 50);

      if (!loaded) {
        import("@/data/courses").then((mod) => {
          setTracks(mod.tracks);
          setLoaded(true);
        });
      }
    }
  }, [open, loaded]);

  const results = useMemo<SearchResult[]>(() => {
    const q = query.toLowerCase().trim();
    if (q.length < 2) return [];

    const out: SearchResult[] = [];

    for (const track of tracks) {
      if (track.name.toLowerCase().includes(q) || track.description.toLowerCase().includes(q)) {
        out.push({ type: "track", label: track.name, description: track.tagline, path: `/track/${track.id}`, trackName: track.name });
      }

      for (const mod of track.modules) {
        if (mod.title.toLowerCase().includes(q) || mod.description.toLowerCase().includes(q)) {
          out.push({ type: "module", label: mod.title, description: mod.description.slice(0, 80) + "…", path: `/track/${track.id}/module/${mod.id}`, trackName: track.name });
        }

        // Tools
        for (const tool of mod.tools) {
          if (tool.toLowerCase().includes(q)) {
            out.push({ type: "tool", label: tool, description: `Tool in ${mod.title}`, path: `/track/${track.id}/module/${mod.id}`, trackName: track.name });
          }
        }

        // Lessons
        for (const lesson of mod.lessons) {
          if (lesson.title.toLowerCase().includes(q) || lesson.description.toLowerCase().includes(q)) {
            out.push({ type: "lesson", label: lesson.title, description: lesson.description.slice(0, 80) + "…", path: `/track/${track.id}/module/${mod.id}`, trackName: track.name });
          }
        }
      }
    }

    // Deduplicate by path+label
    const seen = new Set<string>();
    return out.filter((r) => {
      const key = `${r.path}|${r.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 20);
  }, [query]);

  const typeLabel: Record<SearchResult["type"], string> = {
    track: "trk",
    module: "mod",
    lesson: "lsn",
    tool: "bin",
  };

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-2.5 py-1 border border-border bg-background hover:border-primary/60 hover:text-primary transition-colors text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground"
      >
        <Search className="h-3 w-3" />
        <span className="hidden sm:inline">grep ./*</span>
        <kbd className="hidden sm:inline-flex h-4 items-center border border-border bg-muted/40 px-1 font-mono text-[10px] text-muted-foreground">
          ⌘K
        </kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-background/85" onClick={() => setOpen(false)}>
          <div
            className="relative w-full max-w-2xl mx-4 border border-primary/40 bg-background font-mono"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 py-1 border-b border-border bg-muted/20">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                ┌─[ grep -rni ./frisyre ]
              </span>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-primary text-[10px] uppercase tracking-[0.18em]">
                [esc]
              </button>
            </div>

            <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
              <span className="text-primary text-[12px] shrink-0">root@frisyre:~#</span>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="grep -rni 'pattern' ./tracks ./modules ./tools"
                className="flex-1 bg-transparent text-[12px] text-foreground outline-none placeholder:text-muted-foreground/60 caret-primary"
                spellCheck={false}
                autoComplete="off"
              />
              <span className="text-primary text-[12px] animate-pulse">▍</span>
            </div>

            <div className="max-h-[55vh] overflow-y-auto">
              {query.length < 2 ? (
                <div className="px-3 py-4 text-[11px] text-muted-foreground space-y-1">
                  <div>// awaiting pattern. min 2 chars.</div>
                  <div className="text-muted-foreground/60">// scope: tracks, modules, lessons, tools</div>
                  <div className="text-muted-foreground/60">// keys: ↵ open · esc close</div>
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-4 text-[11px] text-destructive">
                  grep: no matches for "{query}" — exit 1
                </div>
              ) : (
                <div className="py-1">
                  <div className="px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border/60">
                    {results.length} match{results.length === 1 ? "" : "es"}
                  </div>
                  {results.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => go(r.path)}
                      className="w-full grid grid-cols-[auto_auto_1fr_auto] items-center gap-3 px-3 py-1.5 hover:bg-primary/10 hover:text-primary transition-colors text-left border-b border-border/30 last:border-b-0 group"
                    >
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.16em] text-primary/80 border border-primary/30 px-1">
                        {typeLabel[r.type]}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[12px] text-foreground group-hover:text-primary truncate">
                          {r.label}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {r.description}
                        </div>
                      </div>
                      <span className="text-[10px] text-muted-foreground group-hover:text-primary opacity-0 group-hover:opacity-100">
                        ↵
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="px-3 py-1 border-t border-border bg-muted/20 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <span>└─[ exit 0 ]</span>
              <span>frisyre.shell · v2.6.1</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
